<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Tax Information</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Tax</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading tax information...</p>
          </div>

          <div v-else-if="taxData">
            <!-- Year Selector & Actions -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body">
                    <label class="form-label">Select Tax Year</label>
                    <select v-model="selectedYear" @change="loadTaxSummary" class="form-select">
                      <option v-for="year in taxData.availableYears" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body d-flex align-items-center justify-content-end">
                    <button @click="downloadP9Form" class="btn btn-success me-2">
                      <i class="mdi mdi-file-document-outline me-1"></i>
                      Download P9 Form
                    </button>
                    <button @click="loadTaxSummary" class="btn btn-secondary">
                      <i class="mdi mdi-refresh me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tax Summary Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-cash-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Gross Income</p>
                        <h4 class="mb-0">KES {{ formatCurrency(taxData.taxSummary.totalGross) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                          <i class="mdi mdi-calculator"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Taxable Income</p>
                        <h4 class="mb-0">KES {{ formatCurrency(taxData.taxSummary.totalTaxable) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-danger-subtle text-danger rounded-circle fs-3">
                          <i class="mdi mdi-currency-usd-off"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total PAYE</p>
                        <h4 class="mb-0">KES {{ formatCurrency(taxData.taxSummary.totalPaye) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-percent"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Effective Tax Rate</p>
                        <h4 class="mb-0">{{ taxData.taxSummary.effectiveTaxRate }}%</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tax Breakdown & Reliefs -->
            <div class="row">
              <div class="col-lg-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-file-document text-danger me-2"></i>
                      Tax Breakdown
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="taxData.taxes && taxData.taxes.length > 0">
                      <div v-for="(tax, index) in taxData.taxes" :key="index" class="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                        <div>
                          <h6 class="mb-1">{{ tax.component_name }}</h6>
                          <p class="text-muted mb-0 small">{{ tax.occurrence_count }} occurrences</p>
                        </div>
                        <div class="text-end">
                          <h5 class="mb-0 text-danger">KES {{ formatCurrency(tax.total_amount) }}</h5>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-file-document-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No tax data available</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-tag-check text-success me-2"></i>
                      Tax Reliefs
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="taxData.reliefs && taxData.reliefs.length > 0">
                      <div v-for="(relief, index) in taxData.reliefs" :key="index" class="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                        <div>
                          <h6 class="mb-1">{{ relief.component_name }}</h6>
                          <p class="text-muted mb-0 small">{{ relief.occurrence_count }} occurrences</p>
                        </div>
                        <div class="text-end">
                          <h5 class="mb-0 text-success">KES {{ formatCurrency(Math.abs(relief.total_amount)) }}</h5>
                        </div>
                      </div>
                      <div class="bg-success-subtle p-3 rounded mt-3">
                        <div class="d-flex justify-content-between">
                          <strong>Total Reliefs:</strong>
                          <strong class="text-success">KES {{ formatCurrency(taxData.taxSummary.totalReliefs) }}</strong>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-tag-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No relief data available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statutory Deductions -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-shield-account text-primary me-2"></i>
                      Statutory Deductions
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="taxData.statutoryDeductions && taxData.statutoryDeductions.length > 0" class="row">
                      <div v-for="(deduction, index) in taxData.statutoryDeductions" :key="index" class="col-md-4 mb-3">
                        <div class="border rounded p-3">
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-3">
                              <span class="avatar-title bg-primary rounded-circle">
                                <i class="mdi mdi-shield-check font-size-16"></i>
                              </span>
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1">{{ deduction.component_name }}</h6>
                              <p class="text-muted mb-0 small">{{ deduction.occurrence_count }} payments</p>
                            </div>
                          </div>
                          <div class="mt-3">
                            <h4 class="mb-0">KES {{ formatCurrency(deduction.total_amount) }}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-shield-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No statutory deductions found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monthly Breakdown -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-line text-primary me-2"></i>
                      Monthly Tax Breakdown - {{ selectedYear }}
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="taxData.monthlyBreakdown && taxData.monthlyBreakdown.length > 0" class="table-responsive">
                      <table class="table table-hover table-sm mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Month</th>
                          <th class="text-end">Gross Salary</th>
                          <th class="text-end">Taxable Income</th>
                          <th class="text-end">PAYE</th>
                          <th class="text-end">Net Salary</th>
                          <th class="text-end">Tax Rate</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(month, index) in taxData.monthlyBreakdown" :key="index">
                          <td>
                            <strong>{{ month.month_name }}</strong>
                          </td>
                          <td class="text-end">{{ formatCurrency(month.gross_salary) }}</td>
                          <td class="text-end">{{ formatCurrency(month.taxable_income) }}</td>
                          <td class="text-end text-danger">
                            <strong>{{ formatCurrency(month.paye) }}</strong>
                          </td>
                          <td class="text-end text-success">
                            <strong>{{ formatCurrency(month.net_salary) }}</strong>
                          </td>
                          <td class="text-end">
                              <span class="badge bg-info">
                                {{ calculateTaxRate(month.paye, month.gross_salary) }}%
                              </span>
                          </td>
                        </tr>
                        </tbody>
                        <tfoot class="table-light fw-bold">
                        <tr>
                          <td>TOTAL</td>
                          <td class="text-end">{{ formatCurrency(getTotalColumn('gross_salary')) }}</td>
                          <td class="text-end">{{ formatCurrency(getTotalColumn('taxable_income')) }}</td>
                          <td class="text-end text-danger">{{ formatCurrency(getTotalColumn('paye')) }}</td>
                          <td class="text-end text-success">{{ formatCurrency(getTotalColumn('net_salary')) }}</td>
                          <td class="text-end">
                              <span class="badge bg-primary">
                                {{ taxData.taxSummary.effectiveTaxRate }}%
                              </span>
                          </td>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-chart-line font-size-48"></i>
                      <p class="mt-2 mb-0">No monthly data available for {{ selectedYear }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- KRA Information -->
            <div class="row">
              <div class="col-12">
                <div class="card border-primary">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="card-title text-primary mb-2">
                          <i class="mdi mdi-information me-2"></i>
                          KRA Information
                        </h5>
                        <div class="row">
                          <div class="col-md-6 mb-2">
                            <p class="text-muted mb-1 small">Your KRA PIN</p>
                            <h6 class="mb-0">{{ taxData.employee.kraPin || 'Not Set' }}</h6>
                          </div>
                          <div class="col-md-6 mb-2">
                            <p class="text-muted mb-1 small">Employee Number</p>
                            <h6 class="mb-0">{{ taxData.employee.empNo }}</h6>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <a href="https://itax.kra.go.ke/KRA-Portal/" target="_blank" class="btn btn-primary">
                          <i class="mdi mdi-open-in-new me-1"></i>
                          Visit iTax Portal
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollTax",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      taxData: null,
      selectedYear: new Date().getFullYear()
    };
  },

  mounted() {
    this.loadTaxSummary();
  },

  methods: {
    async loadTaxSummary() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeTaxSummary");
        data.append("year", this.selectedYear);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.taxData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load tax summary');
        }
      } catch (error) {
        this.$toast?.error("Failed to load tax summary: " + error.message) ||
        console.error("Failed to load tax summary:", error);
      } finally {
        this.loading = false;
      }
    },

    async downloadP9Form() {
      try {
        this.$toast?.info("Generating P9 form...") || console.log("Generating P9 form...");

        const data = new FormData();
        data.append("function", "getEmployeeP9Form");
        data.append("year", this.selectedYear);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          // Generate PDF from P9 data
          this.generateP9PDF(res.data.data);
        } else {
          throw new Error(res.data?.message || 'Failed to generate P9 form');
        }
      } catch (error) {
        this.$toast?.error("Failed to download P9 form: " + error.message) ||
        alert("Failed to download P9 form: " + error.message);
      }
    },

    async generateP9PDF(p9Data) {
      try {
        // Dynamically import jsPDF
        const jsPDF = (await import('jspdf')).default;
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // Title
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('P9 TAX DEDUCTION CARD', pageWidth / 2, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text(`Year of Income: ${p9Data.year}`, pageWidth / 2, 28, { align: 'center' });

        // Employee & Employer Info
        let startY = 35;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Employee Information:', 15, startY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Name: ${p9Data.employee.fullName}`, 15, startY + 6);
        doc.text(`PIN: ${p9Data.employee.kraPin}`, 15, startY + 12);
        doc.text(`Employee No: ${p9Data.employee.empNo}`, 15, startY + 18);

        doc.setFont(undefined, 'bold');
        doc.setFontSize(10);
        doc.text('Employer Information:', pageWidth / 2 + 10, startY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Name: ${p9Data.employer.name}`, pageWidth / 2 + 10, startY + 6);
        doc.text(`PIN: ${p9Data.employer.pin}`, pageWidth / 2 + 10, startY + 12);

        startY += 30;

        // P9 Table
        const tableData = p9Data.monthlyData.map(month => [
          month.month_name,
          this.formatAmount(month.basic_salary),
          this.formatAmount(month.benefits),
          this.formatAmount(month.total_gross),
          this.formatAmount(month.nssf),
          this.formatAmount(month.nhif),
          this.formatAmount(month.housing_levy),
          this.formatAmount(month.taxable_income),
          this.formatAmount(month.tax_charged),
          this.formatAmount(month.personal_relief),
          this.formatAmount(month.paye)
        ]);

        // Add totals row
        tableData.push([
          'TOTAL',
          this.formatAmount(p9Data.totals.basic_salary),
          this.formatAmount(p9Data.totals.benefits),
          this.formatAmount(p9Data.totals.total_gross),
          this.formatAmount(p9Data.totals.nssf),
          this.formatAmount(p9Data.totals.nhif),
          this.formatAmount(p9Data.totals.housing_levy),
          this.formatAmount(p9Data.totals.taxable_income),
          this.formatAmount(p9Data.totals.tax_charged),
          this.formatAmount(p9Data.totals.personal_relief),
          this.formatAmount(p9Data.totals.paye)
        ]);

        autoTable(doc, {
          startY: startY,
          head: [[
            'Month',
            'Basic',
            'Benefits',
            'Gross',
            'NSSF',
            'NHIF/SHIF',
            'H.Levy',
            'Taxable',
            'Tax',
            'Relief',
            'PAYE'
          ]],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [66, 139, 202],
            fontSize: 7,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 7
          },
          columnStyles: {
            0: { cellWidth: 18, fontStyle: 'bold' },
            1: { cellWidth: 16, halign: 'right' },
            2: { cellWidth: 16, halign: 'right' },
            3: { cellWidth: 16, halign: 'right' },
            4: { cellWidth: 14, halign: 'right' },
            5: { cellWidth: 14, halign: 'right' },
            6: { cellWidth: 14, halign: 'right' },
            7: { cellWidth: 18, halign: 'right' },
            8: { cellWidth: 16, halign: 'right' },
            9: { cellWidth: 16, halign: 'right' },
            10: { cellWidth: 16, halign: 'right', fontStyle: 'bold' }
          },
          didParseCell: (data) => {
            if (data.row.index === tableData.length - 1) {
              data.cell.styles.fillColor = [240, 240, 240];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        // Save PDF
        const fileName = `P9_${p9Data.employee.empNo}_${p9Data.year}.pdf`;
        doc.save(fileName);

        this.$toast?.success("P9 form downloaded successfully") ||
        alert("P9 form downloaded successfully");
      } catch (error) {
        console.error('Error generating P9 PDF:', error);
        this.$toast?.error("Error generating PDF: " + error.message) ||
        alert("Error generating PDF: " + error.message);
      }
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatAmount(value) {
      return new Intl.NumberFormat('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    },

    calculateTaxRate(paye, gross) {
      if (!gross || gross === 0) return '0.0';
      return ((paye / gross) * 100).toFixed(1);
    },

    getTotalColumn(columnName) {
      if (!this.taxData || !this.taxData.monthlyBreakdown) return 0;
      return this.taxData.monthlyBreakdown.reduce((sum, month) => {
        return sum + parseFloat(month[columnName] || 0);
      }, 0);
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-xs {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.font-size-48 {
  font-size: 48px;
}

.font-size-16 {
  font-size: 16px;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>